import { useEffect } from 'react'
import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Badge } from '@/components/ui/badge'
import { BookOpen, Users, CreditCard, Shield, HelpCircle, Mail, Phone, MapPin } from 'lucide-react'

const Help = () => {
  useEffect(() => {
    document.title = 'Help Center | BizMatch'
    const meta = document.querySelector('meta[name="description"]')
    if (meta) meta.setAttribute('content', 'BizMatch Help Center: Guides, FAQs, and support resources for brokers and buyers.')
  }, [])

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-primary mb-4">BizMatch Help Centre</h1>
            <p className="text-xl text-muted-foreground">
              Below are frequently asked questions and guides to help you get the most out of BizMatch. 
              Click on a section to expand it.
            </p>
          </div>

          {/* Contents */}
          <Card className="mb-8 shadow-medium">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Contents
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-2">
                <a href="#getting-started" className="text-primary hover:text-primary-light transition-colors">• Getting Started</a>
                <a href="#for-brokers" className="text-primary hover:text-primary-light transition-colors">• For Brokers</a>
                <a href="#for-buyers" className="text-primary hover:text-primary-light transition-colors">• For Buyers</a>
                <a href="#billing" className="text-primary hover:text-primary-light transition-colors">• Billing & Payments</a>
                <a href="#privacy" className="text-primary hover:text-primary-light transition-colors">• Privacy & Security</a>
                <a href="#faqs" className="text-primary hover:text-primary-light transition-colors">• Frequently Asked Questions</a>
              </div>
            </CardContent>
          </Card>

          {/* Getting Started */}
          <Card className="mb-8 shadow-medium" id="getting-started">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-primary" />
                Getting Started
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="font-semibold text-lg mb-2">What is BizMatch?</h3>
                <p className="text-muted-foreground">
                  BizMatch is Australia's smart business-for-sale marketplace. Sellers and brokers list businesses, 
                  and buyers are matched to opportunities using AI + direct outreach.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-lg mb-2">Who can use BizMatch?</h3>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <Badge variant="secondary" className="mt-1">Brokers / Sellers</Badge>
                    <p className="text-muted-foreground">List businesses, manage leads, and receive direct enquiries.</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Badge variant="secondary" className="mt-1">Buyers</Badge>
                    <p className="text-muted-foreground">Create a profile, set your budget and preferences, and get matched with businesses.</p>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold text-lg mb-2">How do I create an account?</h3>
                <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                  <li>Click "Sign Up" on the homepage.</li>
                  <li>Verify your email.</li>
                  <li>Complete your profile (role, budget, preferences, etc).</li>
                </ol>
              </div>
            </CardContent>
          </Card>

          {/* For Brokers */}
          <Card className="mb-8 shadow-medium" id="for-brokers">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                For Brokers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="list-business">
                  <AccordionTrigger>How do I list a business?</AccordionTrigger>
                  <AccordionContent>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Sign in → Broker Portal → "Create Listing."</li>
                      <li>Fill in details (title, sector, location, financials, description).</li>
                      <li>Upload images.</li>
                      <li>Click <strong>Publish</strong> (requires subscription).</li>
                    </ol>
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="cost">
                  <AccordionTrigger>What does it cost to list?</AccordionTrigger>
                  <AccordionContent>
                    <ul className="list-disc list-inside space-y-1">
                      <li>$98/month per live listing for private sellers, $89/month for brokers.</li>
                      <li>$15-$19 per qualified enquiry (lead) depending on package.</li>
                      <li>No commissions, no lock-in contracts.</li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="manage-leads">
                  <AccordionTrigger>How do I manage leads?</AccordionTrigger>
                  <AccordionContent>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Leads appear in your <strong>Broker Portal → Leads Inbox</strong>.</li>
                      <li>You can track, qualify, and see enquiry details.</li>
                      <li>Each new lead triggers a $15-$19 fee automatically billed based on your package.</li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="pause-listing">
                  <AccordionTrigger>Can I pause or remove a listing?</AccordionTrigger>
                  <AccordionContent>
                    Yes — in Broker Portal, change listing status to <strong>Paused</strong>, <strong>Draft</strong>, or <strong>Sold</strong>. 
                    Billing adjusts when listing is not live.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          {/* For Buyers */}
          <Card className="mb-8 shadow-medium" id="for-buyers">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-secondary" />
                For Buyers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="find-businesses">
                  <AccordionTrigger>How do I find businesses?</AccordionTrigger>
                  <AccordionContent>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Use the <strong>Explore</strong> page to browse live listings.</li>
                      <li>Apply filters (location, price, sector).</li>
                      <li>Or create a <strong>Buyer Profile</strong> for AI-powered matches.</li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="enquire">
                  <AccordionTrigger>What happens when I enquire?</AccordionTrigger>
                  <AccordionContent>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Click <strong>Express Interest</strong> on a listing.</li>
                      <li>Your details are sent directly to the broker/seller.</li>
                      <li>They'll contact you with more information.</li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="buyer-cost">
                  <AccordionTrigger>Do I need to pay?</AccordionTrigger>
                  <AccordionContent>
                    No. BizMatch is free for buyers.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          {/* Billing & Payments */}
          <Card className="mb-8 shadow-medium" id="billing">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-primary" />
                Billing & Payments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="subscriptions">
                  <AccordionTrigger>How do subscriptions work?</AccordionTrigger>
                  <AccordionContent>
                    Each live listing = $98/month for private sellers, $89/month for brokers, billed via Stripe. Payments auto-renew monthly until you pause or remove the listing.
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="leads-charged">
                  <AccordionTrigger>How are leads charged?</AccordionTrigger>
                  <AccordionContent>
                    Each enquiry = $15-$19 based on your package, invoiced instantly via Stripe.
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="billing-details">
                  <AccordionTrigger>Where can I update my billing details?</AccordionTrigger>
                  <AccordionContent>
                    Go to Broker Portal → <strong>Billing Settings</strong> to view and update payment methods and see invoice history.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          {/* Privacy & Security */}
          <Card className="mb-8 shadow-medium" id="privacy">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                Privacy & Security
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="data-protection">
                  <AccordionTrigger>How is my data protected?</AccordionTrigger>
                  <AccordionContent>
                    We use secure hosting and encryption. All personal data is stored securely. Only verified brokers can access leads.
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="data-sharing">
                  <AccordionTrigger>Do you share my data?</AccordionTrigger>
                  <AccordionContent>
                    We do not sell your personal data. We may share it with service providers for email, payments, analytics, 
                    or if required by law. Check our Privacy Policy for full details.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          {/* FAQs */}
          <Card className="mb-8 shadow-medium" id="faqs">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HelpCircle className="h-5 w-5 text-primary" />
                Frequently Asked Questions (FAQs)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="different">
                  <AccordionTrigger>What makes BizMatch different?</AccordionTrigger>
                  <AccordionContent>
                    BizMatch charges no commissions and no lock-in contracts. You pay only for the listing and leads; 
                    we actively match buyers to sellers using AI and direct outreach.
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="pricing-structure">
                  <AccordionTrigger>How is pricing structured?</AccordionTrigger>
                  <AccordionContent>
                    The cost is $98 per listing per month for private sellers, $89 for brokers, plus $15-$19 per buyer enquiry (lead) based on your package.
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="anyone-list">
                  <AccordionTrigger>Can anyone list their business for sale?</AccordionTrigger>
                  <AccordionContent>
                    Yes, private business owners can list their business for sale on BizMatch. You don't need to be a licensed broker to list your business.
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="contact-support">
                  <AccordionTrigger>How do I contact support?</AccordionTrigger>
                  <AccordionContent>
                    You can reach us via Email: Hello@BizMatch.com.au or Phone: +61 418 616 704. 
                    Our office address is 2/290 Boundary St, Spring Hill, QLD 4000.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          {/* Contact & Further Help */}
          <Card className="shadow-medium">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                Contact & Further Help
              </CardTitle>
              <CardDescription>
                If you don't find what you're looking for, reach out:
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-primary" />
                  <div>
                    <p className="font-medium">Email</p>
                    <p className="text-sm text-muted-foreground">Hello@BizMatch.com.au</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-primary" />
                  <div>
                    <p className="font-medium">Phone</p>
                    <p className="text-sm text-muted-foreground">+61 418 616 704</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary" />
                  <div>
                    <p className="font-medium">Address</p>
                    <p className="text-sm text-muted-foreground">2/290 Boundary St, Spring Hill, QLD 4000</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default Help
